import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

export const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "al", label: "Shqip", flag: "🇦🇱" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

const DICT = {
  en: {
    "nav.stay": "Stay", "nav.rooms": "Rooms", "nav.about": "About",
    "nav.contact": "Contact", "nav.policies": "Policies", "nav.reviews": "Reviews",
    "nav.signin": "Sign in", "nav.booknow": "Book a room", "nav.mybookings": "My bookings",
    "nav.logout": "Logout", "nav.staff": "Staff",
    "widget.checkin": "Check-in", "widget.checkout": "Check-out", "widget.guests": "Guests",
    "widget.search": "Search rooms", "widget.guest": "guest", "widget.guests_plural": "guests",
    "home.location": "Sarandë · Albania",
    "home.welcome": "Welcome", "home.intro_title": "An intimate retreat on the Albanian Riviera",
    "home.feat.sea": "Sea views", "home.feat.wifi": "Fast Wi-Fi", "home.feat.walk": "10 min from sea",
    "home.staywithus": "Stay with us", "home.our_rooms": "Our rooms & apartments", "home.view_all": "View all rooms",
    "home.mornings": "Mornings", "home.mornings_title": "Sea breeze, slow mornings",
    "home.mornings_text": "Step onto the rooftop terrace to watch the Ionian wake up — soft light over the bay, fishing boats drifting in, and the first ferry leaving for Corfu.",
    "home.more_about": "More about Berdhill",
    "home.cta_kicker": "Reserve your stay",
    "home.cta_title": "We are open all year, with a small number of rooms.",
    "home.cta_sub": "Apple Pay, Google Pay or card — book in under two minutes.",
    "home.cta_btn": "Check availability",
    "home.reviews_kicker": "What guests say", "home.reviews_title": "Loved by our guests",
    "rooms.title": "Our rooms & apartments",
    "rooms.subtitle": "Choose your dates and number of guests. Every room includes fast Wi-Fi and air conditioning.",
    "rooms.empty": "No rooms match your search.",
    "rooms.empty_hint": "Try fewer guests or different dates.",
    "rooms.per_night": "per night",
    "detail.back": "← Back", "detail.room": "Room",
    "detail.amenities": "Amenities",
    "detail.check_avail": "Check availability", "detail.available": "Available for these dates ✓",
    "detail.not_avail": "Not available — try different dates.",
    "detail.reserve": "Reserve",
    "detail.free_cancel": "Free cancellation up to 7 days before check-in.",
    "detail.calendar": "Availability calendar",
    "detail.blocked": "Booked", "detail.free": "Available",
    "book.reservation": "Reservation", "book.book_title": "Book",
    "book.step1": "Dates", "book.step2": "Guest details", "book.step3": "Review & pay",
    "book.when": "When will you stay?", "book.continue": "Continue", "book.back": "Back",
    "book.name": "Full name", "book.email": "Email", "book.phone": "Phone (optional)",
    "book.requests": "Special requests (optional)",
    "book.review": "Review & pay",
    "book.secure_pay": "Secure payment",
    "book.secure_text": "You will be redirected to our Stripe checkout to complete the payment with Apple Pay, Google Pay or card.",
    "book.pay": "Pay",
    "success.checking": "Confirming your payment…",
    "success.dont_close": "Please don't close this page.",
    "success.confirmed": "Reservation confirmed",
    "success.thanks": "Thank you for choosing Berdhill Rooms. We've sent a confirmation to your email. We look forward to welcoming you in Sarandë.",
    "success.view_bookings": "View my bookings", "success.return": "Return home",
    "success.processing": "Still processing…",
    "success.failed": "Payment not completed",
    "success.failed_text": "Your session was cancelled or expired. You can try again at any time.",
    "success.back_rooms": "Back to rooms",
    "mybookings.welcome": "Welcome back",
    "mybookings.title": "My bookings", "mybookings.empty": "No bookings yet",
    "mybookings.find": "Find a room",
    "mybookings.cancel": "Cancel booking",
    "mybookings.write_review": "Write a review",
    "review.title": "Reviews", "review.kicker": "Real stays, real words",
    "review.write": "Write a review", "review.submit": "Submit review",
    "review.rating": "Your rating", "review.title_field": "Headline (optional)",
    "review.content": "Your stay in your own words…",
    "review.thanks": "Thanks for your review — it will appear after moderation.",
    "review.signin_first": "Sign in with your booking account to leave a review.",
    "review.no_booking": "Only confirmed guests can leave a review.",
    "review.guest_count": "{count} verified stays",
    "review.avg": "{avg} / 5 average",
    "contact.title": "Get in touch", "contact.subtitle": "Contact",
    "contact.send": "Send message", "contact.name": "Your name", "contact.subj": "Subject",
    "contact.message": "Message", "contact.sent": "Thank you — we'll be in touch shortly.",
    "auth.welcome_back": "Welcome back",
    "auth.signin": "Sign in", "auth.signin_sub": "Sign in to manage your bookings",
    "auth.signup": "Create account", "auth.signup_sub": "Save your details for faster booking",
    "auth.staff_portal": "Staff portal", "auth.staff_sub": "Sign in to manage Berdhill Rooms",
    "auth.have_account": "Have an account? Sign in", "auth.new_here": "New here? Create an account",
    "footer.explore": "Explore", "footer.policies": "Policies", "footer.visit": "Visit",
    "footer.tagline": "A boutique stay on the Albanian Riviera. Sarandë, above the Ionian Sea.",
  },
  al: {
    "nav.stay": "Qëndrimi", "nav.rooms": "Dhomat", "nav.about": "Rreth nesh",
    "nav.contact": "Kontakt", "nav.policies": "Politikat", "nav.reviews": "Vlerësimet",
    "nav.signin": "Hyr", "nav.booknow": "Rezervo një dhomë", "nav.mybookings": "Rezervimet e mia",
    "nav.logout": "Dil", "nav.staff": "Stafi",
    "widget.checkin": "Mbërritja", "widget.checkout": "Largimi", "widget.guests": "Mysafirë",
    "widget.search": "Kërko dhoma", "widget.guest": "mysafir", "widget.guests_plural": "mysafirë",
    "home.location": "Sarandë · Shqipëri",
    "home.welcome": "Mirë se vini", "home.intro_title": "Një strehë intime në Rivierën Shqiptare",
    "home.feat.sea": "Pamje deti", "home.feat.wifi": "Wi-Fi i shpejtë", "home.feat.walk": "10 min nga deti",
    "home.staywithus": "Qëndro me ne", "home.our_rooms": "Dhomat dhe apartamentet tona", "home.view_all": "Shiko të gjitha",
    "home.mornings": "Mëngjeset", "home.mornings_title": "Freskia e detit, mëngjese të qeta",
    "home.mornings_text": "Dil në terracë në mëngjes për të parë Jonin që zgjohet — dritë e butë mbi gji, varka peshkimi që afrohen dhe trageti i parë drejt Korfuzit.",
    "home.more_about": "Më shumë rreth Berdhill",
    "home.cta_kicker": "Rezervo qëndrimin", "home.cta_title": "Jemi të hapur gjithë vitin, me një numër të vogël dhomash.",
    "home.cta_sub": "Apple Pay, Google Pay ose kartë — rezervo në më pak se dy minuta.",
    "home.cta_btn": "Kontrollo disponueshmërinë",
    "home.reviews_kicker": "Çfarë thonë mysafirët", "home.reviews_title": "Të dashur nga mysafirët tanë",
    "rooms.title": "Dhomat dhe apartamentet tona",
    "rooms.subtitle": "Zgjidh datat dhe numrin e mysafirëve. Çdo dhomë ka Wi-Fi të shpejtë dhe ajër të kondicionuar.",
    "rooms.empty": "Asnjë dhomë nuk përputhet me kërkimin tuaj.",
    "rooms.empty_hint": "Provoni më pak mysafirë ose data të tjera.",
    "rooms.per_night": "për natë",
    "detail.back": "← Kthehu", "detail.room": "Dhoma", "detail.amenities": "Komoditete",
    "detail.check_avail": "Kontrollo disponueshmërinë", "detail.available": "E disponueshme ✓",
    "detail.not_avail": "E pa-disponueshme — provo data të tjera.", "detail.reserve": "Rezervo",
    "detail.free_cancel": "Anulim falas deri 7 ditë para mbërritjes.",
    "detail.calendar": "Kalendari i disponueshmërisë",
    "detail.blocked": "E rezervuar", "detail.free": "E lirë",
    "book.reservation": "Rezervimi", "book.book_title": "Rezervo",
    "book.step1": "Datat", "book.step2": "Të dhënat", "book.step3": "Rishiko & paguaj",
    "book.when": "Kur do të qëndroni?", "book.continue": "Vazhdo", "book.back": "Kthehu",
    "book.name": "Emri i plotë", "book.email": "Email", "book.phone": "Telefoni (opsional)",
    "book.requests": "Kërkesa të veçanta (opsional)", "book.review": "Rishiko & paguaj",
    "book.secure_pay": "Pagesë e sigurt",
    "book.secure_text": "Do të ridrejtoheni në Stripe për të paguar me Apple Pay, Google Pay ose kartë.",
    "book.pay": "Paguaj",
    "success.checking": "Po konfirmojmë pagesën…", "success.dont_close": "Mos e mbyll këtë faqe.",
    "success.confirmed": "Rezervimi u konfirmua",
    "success.thanks": "Faleminderit që zgjodhët Berdhill Rooms. Ju kemi dërguar konfirmimin me email.",
    "success.view_bookings": "Shiko rezervimet", "success.return": "Kthehu në shtëpi",
    "success.processing": "Ende po përpunohet…", "success.failed": "Pagesa nuk u krye",
    "success.failed_text": "Seanca u anulua ose skadoi. Mund të provoni përsëri.",
    "success.back_rooms": "Kthehu te dhomat",
    "mybookings.welcome": "Mirë se erdhe", "mybookings.title": "Rezervimet e mia",
    "mybookings.empty": "Ende asnjë rezervim", "mybookings.find": "Gjej një dhomë",
    "mybookings.cancel": "Anulo rezervimin", "mybookings.write_review": "Shkruaj vlerësim",
    "review.title": "Vlerësimet", "review.kicker": "Qëndrime të vërteta, fjalë të vërteta",
    "review.write": "Shkruaj vlerësim", "review.submit": "Dërgo vlerësimin",
    "review.rating": "Vlerësimi yt", "review.title_field": "Titulli (opsional)",
    "review.content": "Qëndrimi yt me fjalët e tua…",
    "review.thanks": "Faleminderit — vlerësimi do të shfaqet pas moderimit.",
    "review.signin_first": "Hyr me llogarinë e rezervimit për të lënë vlerësim.",
    "review.no_booking": "Vetëm mysafirët e konfirmuar mund të lënë vlerësim.",
    "review.guest_count": "{count} qëndrime të verifikuara",
    "review.avg": "{avg} / 5 mesatare",
    "contact.title": "Na shkruaj", "contact.subtitle": "Kontakt",
    "contact.send": "Dërgo mesazhin", "contact.name": "Emri yt",
    "contact.subj": "Tema", "contact.message": "Mesazhi",
    "contact.sent": "Faleminderit — do t'ju kontaktojmë shpejt.",
    "auth.welcome_back": "Mirë se erdhe", "auth.signin": "Hyr",
    "auth.signin_sub": "Hyr për të menaxhuar rezervimet tuaja",
    "auth.signup": "Krijo llogari", "auth.signup_sub": "Ruaj të dhënat për rezervim më të shpejtë",
    "auth.staff_portal": "Portali i stafit", "auth.staff_sub": "Hyr për të menaxhuar Berdhill Rooms",
    "auth.have_account": "Ke llogari? Hyr", "auth.new_here": "I ri këtu? Krijo llogari",
    "footer.explore": "Eksploro", "footer.policies": "Politikat", "footer.visit": "Na vizito",
    "footer.tagline": "Një qëndrim butik në Rivierën Shqiptare. Sarandë, mbi detin Jon.",
  },
  it: {
    "nav.stay": "Soggiorno", "nav.rooms": "Camere", "nav.about": "Chi siamo",
    "nav.contact": "Contatti", "nav.policies": "Policy", "nav.reviews": "Recensioni",
    "nav.signin": "Accedi", "nav.booknow": "Prenota", "nav.mybookings": "Le mie prenotazioni",
    "nav.logout": "Esci", "nav.staff": "Staff",
    "widget.checkin": "Check-in", "widget.checkout": "Check-out", "widget.guests": "Ospiti",
    "widget.search": "Cerca camere", "widget.guest": "ospite", "widget.guests_plural": "ospiti",
    "home.location": "Saranda · Albania",
    "home.welcome": "Benvenuti", "home.intro_title": "Un rifugio intimo sulla Riviera Albanese",
    "home.feat.sea": "Vista mare", "home.feat.wifi": "Wi-Fi veloce", "home.feat.walk": "10 min dal mare",
    "home.staywithus": "Resta con noi", "home.our_rooms": "Le nostre camere e appartamenti", "home.view_all": "Vedi tutte",
    "home.mornings": "Mattine", "home.mornings_title": "Brezza marina, mattine lente",
    "home.mornings_text": "Affacciati sulla terrazza per veder svegliarsi lo Ionio — luce dolce sulla baia, le prime barche da pesca e il traghetto che parte per Corfù.",
    "home.more_about": "Scopri Berdhill", "home.cta_kicker": "Prenota il tuo soggiorno",
    "home.cta_title": "Aperti tutto l'anno, con poche camere.",
    "home.cta_sub": "Apple Pay, Google Pay o carta — prenota in due minuti.",
    "home.cta_btn": "Verifica disponibilità",
    "home.reviews_kicker": "Cosa dicono gli ospiti", "home.reviews_title": "Amati dai nostri ospiti",
    "rooms.title": "Le nostre camere e appartamenti",
    "rooms.subtitle": "Scegli date e numero di ospiti. Wi-Fi veloce e aria condizionata inclusi.",
    "rooms.empty": "Nessuna camera corrisponde.",
    "rooms.empty_hint": "Prova meno ospiti o altre date.", "rooms.per_night": "a notte",
    "detail.back": "← Indietro", "detail.room": "Camera", "detail.amenities": "Servizi",
    "detail.check_avail": "Verifica disponibilità", "detail.available": "Disponibile ✓",
    "detail.not_avail": "Non disponibile — prova altre date.", "detail.reserve": "Prenota",
    "detail.free_cancel": "Cancellazione gratuita fino a 7 giorni prima.",
    "detail.calendar": "Calendario disponibilità", "detail.blocked": "Occupato", "detail.free": "Libero",
    "book.reservation": "Prenotazione", "book.book_title": "Prenota",
    "book.step1": "Date", "book.step2": "Dati ospite", "book.step3": "Riepilogo & paga",
    "book.when": "Quando soggiornerete?", "book.continue": "Continua", "book.back": "Indietro",
    "book.name": "Nome completo", "book.email": "Email", "book.phone": "Telefono (opzionale)",
    "book.requests": "Richieste speciali (opzionale)", "book.review": "Riepilogo & paga",
    "book.secure_pay": "Pagamento sicuro",
    "book.secure_text": "Verrai reindirizzato a Stripe per pagare con Apple Pay, Google Pay o carta.",
    "book.pay": "Paga",
    "success.checking": "Conferma pagamento…", "success.dont_close": "Non chiudere la pagina.",
    "success.confirmed": "Prenotazione confermata",
    "success.thanks": "Grazie per aver scelto Berdhill Rooms. Conferma inviata via email.",
    "success.view_bookings": "Vedi prenotazioni", "success.return": "Home",
    "success.processing": "In elaborazione…", "success.failed": "Pagamento non completato",
    "success.failed_text": "La sessione è scaduta o annullata. Riprova quando vuoi.",
    "success.back_rooms": "Torna alle camere",
    "mybookings.welcome": "Bentornato", "mybookings.title": "Le mie prenotazioni",
    "mybookings.empty": "Nessuna prenotazione", "mybookings.find": "Trova una camera",
    "mybookings.cancel": "Annulla", "mybookings.write_review": "Scrivi recensione",
    "review.title": "Recensioni", "review.kicker": "Soggiorni veri, parole vere",
    "review.write": "Scrivi recensione", "review.submit": "Invia recensione",
    "review.rating": "Voto", "review.title_field": "Titolo (opzionale)",
    "review.content": "Il tuo soggiorno…",
    "review.thanks": "Grazie — la recensione apparirà dopo moderazione.",
    "review.signin_first": "Accedi con l'account della prenotazione per recensire.",
    "review.no_booking": "Solo gli ospiti confermati possono recensire.",
    "review.guest_count": "{count} soggiorni verificati", "review.avg": "{avg} / 5 media",
    "contact.title": "Contattaci", "contact.subtitle": "Contatti",
    "contact.send": "Invia", "contact.name": "Nome",
    "contact.subj": "Oggetto", "contact.message": "Messaggio",
    "contact.sent": "Grazie — ti contatteremo presto.",
    "auth.welcome_back": "Bentornato", "auth.signin": "Accedi",
    "auth.signin_sub": "Accedi per gestire le tue prenotazioni",
    "auth.signup": "Crea account", "auth.signup_sub": "Salva i tuoi dati per prenotare più veloce",
    "auth.staff_portal": "Portale staff", "auth.staff_sub": "Accedi per gestire Berdhill Rooms",
    "auth.have_account": "Hai un account? Accedi", "auth.new_here": "Nuovo? Crea un account",
    "footer.explore": "Esplora", "footer.policies": "Policy", "footer.visit": "Vienici a trovare",
    "footer.tagline": "Soggiorno boutique sulla Riviera Albanese, sopra lo Ionio.",
  },
  pl: {
    "nav.stay": "Pobyt", "nav.rooms": "Pokoje", "nav.about": "O nas",
    "nav.contact": "Kontakt", "nav.policies": "Regulamin", "nav.reviews": "Opinie",
    "nav.signin": "Zaloguj", "nav.booknow": "Zarezerwuj", "nav.mybookings": "Moje rezerwacje",
    "nav.logout": "Wyloguj", "nav.staff": "Personel",
    "widget.checkin": "Przyjazd", "widget.checkout": "Wyjazd", "widget.guests": "Goście",
    "widget.search": "Szukaj pokoi", "widget.guest": "gość", "widget.guests_plural": "gości",
    "home.location": "Saranda · Albania",
    "home.welcome": "Witamy", "home.intro_title": "Kameralne miejsce na Riwierze Albańskiej",
    "home.feat.sea": "Widok na morze", "home.feat.wifi": "Szybkie Wi-Fi", "home.feat.walk": "10 min do morza",
    "home.staywithus": "Zostań z nami", "home.our_rooms": "Nasze pokoje i apartamenty", "home.view_all": "Zobacz wszystkie",
    "home.mornings": "Poranki", "home.mornings_title": "Morska bryza, leniwe poranki",
    "home.mornings_text": "Wyjdź na dachowy taras i obserwuj, jak budzi się Morze Jońskie — miękkie światło nad zatoką, łodzie rybackie i pierwszy prom do Korfu.",
    "home.more_about": "Więcej o Berdhill", "home.cta_kicker": "Zarezerwuj pobyt",
    "home.cta_title": "Otwarte cały rok, ograniczona liczba pokoi.",
    "home.cta_sub": "Apple Pay, Google Pay lub karta — rezerwacja w 2 minuty.",
    "home.cta_btn": "Sprawdź dostępność",
    "home.reviews_kicker": "Co mówią goście", "home.reviews_title": "Uwielbiani przez gości",
    "rooms.title": "Nasze pokoje i apartamenty",
    "rooms.subtitle": "Wybierz daty i liczbę gości. Szybkie Wi-Fi i klimatyzacja w cenie.",
    "rooms.empty": "Brak pokoi.",
    "rooms.empty_hint": "Spróbuj mniej gości lub innych dat.", "rooms.per_night": "za noc",
    "detail.back": "← Wstecz", "detail.room": "Pokój", "detail.amenities": "Udogodnienia",
    "detail.check_avail": "Sprawdź dostępność", "detail.available": "Dostępne ✓",
    "detail.not_avail": "Niedostępne — spróbuj innych dat.", "detail.reserve": "Rezerwuj",
    "detail.free_cancel": "Bezpłatne anulowanie do 7 dni przed przyjazdem.",
    "detail.calendar": "Kalendarz dostępności", "detail.blocked": "Zajęte", "detail.free": "Wolne",
    "book.reservation": "Rezerwacja", "book.book_title": "Zarezerwuj",
    "book.step1": "Daty", "book.step2": "Dane gościa", "book.step3": "Podsumowanie & zapłać",
    "book.when": "Kiedy przyjedziesz?", "book.continue": "Dalej", "book.back": "Wstecz",
    "book.name": "Imię i nazwisko", "book.email": "Email", "book.phone": "Telefon (opcjonalnie)",
    "book.requests": "Życzenia specjalne (opcjonalnie)", "book.review": "Podsumowanie & zapłać",
    "book.secure_pay": "Bezpieczna płatność",
    "book.secure_text": "Zostaniesz przekierowany do Stripe, aby zapłacić Apple Pay, Google Pay lub kartą.",
    "book.pay": "Zapłać",
    "success.checking": "Potwierdzamy płatność…", "success.dont_close": "Nie zamykaj strony.",
    "success.confirmed": "Rezerwacja potwierdzona",
    "success.thanks": "Dziękujemy. Potwierdzenie zostało wysłane na e-mail.",
    "success.view_bookings": "Zobacz rezerwacje", "success.return": "Strona główna",
    "success.processing": "Przetwarzanie…", "success.failed": "Płatność nieudana",
    "success.failed_text": "Sesja anulowana lub wygasła. Spróbuj ponownie.",
    "success.back_rooms": "Wróć do pokoi",
    "mybookings.welcome": "Witaj ponownie", "mybookings.title": "Moje rezerwacje",
    "mybookings.empty": "Brak rezerwacji", "mybookings.find": "Znajdź pokój",
    "mybookings.cancel": "Anuluj rezerwację", "mybookings.write_review": "Dodaj opinię",
    "review.title": "Opinie", "review.kicker": "Prawdziwe pobyty, prawdziwe słowa",
    "review.write": "Dodaj opinię", "review.submit": "Wyślij opinię",
    "review.rating": "Ocena", "review.title_field": "Tytuł (opcjonalnie)",
    "review.content": "Twój pobyt…",
    "review.thanks": "Dzięki — opinia pojawi się po moderacji.",
    "review.signin_first": "Zaloguj się kontem rezerwacji, aby dodać opinię.",
    "review.no_booking": "Tylko potwierdzeni goście mogą dodać opinię.",
    "review.guest_count": "{count} zweryfikowanych pobytów", "review.avg": "{avg} / 5 średnia",
    "contact.title": "Skontaktuj się", "contact.subtitle": "Kontakt",
    "contact.send": "Wyślij", "contact.name": "Imię",
    "contact.subj": "Temat", "contact.message": "Wiadomość",
    "contact.sent": "Dziękujemy — wkrótce odpowiemy.",
    "auth.welcome_back": "Witaj ponownie", "auth.signin": "Zaloguj",
    "auth.signin_sub": "Zaloguj się, by zarządzać rezerwacjami",
    "auth.signup": "Załóż konto", "auth.signup_sub": "Zapisz dane dla szybszych rezerwacji",
    "auth.staff_portal": "Portal personelu", "auth.staff_sub": "Zaloguj się, by zarządzać Berdhill Rooms",
    "auth.have_account": "Masz konto? Zaloguj", "auth.new_here": "Nowy? Załóż konto",
    "footer.explore": "Odkryj", "footer.policies": "Regulamin", "footer.visit": "Odwiedź nas",
    "footer.tagline": "Butikowy pobyt na Riwierze Albańskiej, nad Morzem Jońskim.",
  },
  fr: {
    "nav.stay": "Séjour", "nav.rooms": "Chambres", "nav.about": "À propos",
    "nav.contact": "Contact", "nav.policies": "Politiques", "nav.reviews": "Avis",
    "nav.signin": "Connexion", "nav.booknow": "Réserver", "nav.mybookings": "Mes réservations",
    "nav.logout": "Déconnexion", "nav.staff": "Personnel",
    "widget.checkin": "Arrivée", "widget.checkout": "Départ", "widget.guests": "Voyageurs",
    "widget.search": "Rechercher", "widget.guest": "voyageur", "widget.guests_plural": "voyageurs",
    "home.location": "Sarandë · Albanie",
    "home.welcome": "Bienvenue", "home.intro_title": "Une retraite intime sur la Riviera Albanaise",
    "home.feat.sea": "Vue mer", "home.feat.wifi": "Wi-Fi rapide", "home.feat.walk": "10 min de la mer",
    "home.staywithus": "Restez avec nous", "home.our_rooms": "Nos chambres & appartements", "home.view_all": "Tout voir",
    "home.mornings": "Matinées", "home.mornings_title": "Brise marine, matinées lentes",
    "home.mornings_text": "Montez sur la terrasse pour voir l'Ionienne s'éveiller — lumière douce sur la baie, premiers bateaux de pêche et le ferry qui part pour Corfou.",
    "home.more_about": "Plus sur Berdhill", "home.cta_kicker": "Réservez votre séjour",
    "home.cta_title": "Ouvert toute l'année, peu de chambres.",
    "home.cta_sub": "Apple Pay, Google Pay ou carte — réservez en 2 minutes.",
    "home.cta_btn": "Vérifier la disponibilité",
    "home.reviews_kicker": "Ce que disent les voyageurs", "home.reviews_title": "Aimés de nos hôtes",
    "rooms.title": "Nos chambres & appartements",
    "rooms.subtitle": "Choisissez vos dates et le nombre de voyageurs. Wi-Fi rapide et climatisation inclus.",
    "rooms.empty": "Aucune chambre.",
    "rooms.empty_hint": "Essayez moins de voyageurs ou d'autres dates.", "rooms.per_night": "par nuit",
    "detail.back": "← Retour", "detail.room": "Chambre", "detail.amenities": "Équipements",
    "detail.check_avail": "Vérifier disponibilité", "detail.available": "Disponible ✓",
    "detail.not_avail": "Indisponible — essayez d'autres dates.", "detail.reserve": "Réserver",
    "detail.free_cancel": "Annulation gratuite jusqu'à 7 jours avant l'arrivée.",
    "detail.calendar": "Calendrier de disponibilité", "detail.blocked": "Occupé", "detail.free": "Libre",
    "book.reservation": "Réservation", "book.book_title": "Réserver",
    "book.step1": "Dates", "book.step2": "Coordonnées", "book.step3": "Récap. & payer",
    "book.when": "Quand séjournez-vous ?", "book.continue": "Continuer", "book.back": "Retour",
    "book.name": "Nom complet", "book.email": "Email", "book.phone": "Téléphone (optionnel)",
    "book.requests": "Demandes spéciales (optionnel)", "book.review": "Récap. & payer",
    "book.secure_pay": "Paiement sécurisé",
    "book.secure_text": "Vous serez redirigé vers Stripe pour payer avec Apple Pay, Google Pay ou carte.",
    "book.pay": "Payer",
    "success.checking": "Confirmation du paiement…", "success.dont_close": "Ne fermez pas cette page.",
    "success.confirmed": "Réservation confirmée",
    "success.thanks": "Merci d'avoir choisi Berdhill Rooms. Confirmation envoyée par e-mail.",
    "success.view_bookings": "Voir mes réservations", "success.return": "Accueil",
    "success.processing": "En cours…", "success.failed": "Paiement non finalisé",
    "success.failed_text": "Session annulée ou expirée. Réessayez quand vous voulez.",
    "success.back_rooms": "Retour aux chambres",
    "mybookings.welcome": "Bon retour", "mybookings.title": "Mes réservations",
    "mybookings.empty": "Pas encore de réservation", "mybookings.find": "Trouver une chambre",
    "mybookings.cancel": "Annuler", "mybookings.write_review": "Laisser un avis",
    "review.title": "Avis", "review.kicker": "Vrais séjours, vrais mots",
    "review.write": "Laisser un avis", "review.submit": "Envoyer l'avis",
    "review.rating": "Votre note", "review.title_field": "Titre (optionnel)",
    "review.content": "Votre séjour…",
    "review.thanks": "Merci — votre avis paraîtra après modération.",
    "review.signin_first": "Connectez-vous avec le compte de réservation pour laisser un avis.",
    "review.no_booking": "Seuls les voyageurs confirmés peuvent laisser un avis.",
    "review.guest_count": "{count} séjours vérifiés", "review.avg": "{avg} / 5 moyen",
    "contact.title": "Nous contacter", "contact.subtitle": "Contact",
    "contact.send": "Envoyer", "contact.name": "Nom",
    "contact.subj": "Objet", "contact.message": "Message",
    "contact.sent": "Merci — nous reviendrons vers vous rapidement.",
    "auth.welcome_back": "Bon retour", "auth.signin": "Se connecter",
    "auth.signin_sub": "Connectez-vous pour gérer vos réservations",
    "auth.signup": "Créer un compte", "auth.signup_sub": "Enregistrez vos infos pour réserver plus vite",
    "auth.staff_portal": "Portail staff", "auth.staff_sub": "Connectez-vous pour gérer Berdhill Rooms",
    "auth.have_account": "Vous avez un compte ? Connexion", "auth.new_here": "Nouveau ? Créez un compte",
    "footer.explore": "Explorer", "footer.policies": "Politiques", "footer.visit": "Nous rendre visite",
    "footer.tagline": "Un séjour boutique sur la Riviera Albanaise, au-dessus de l'Ionienne.",
  },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("berdhill_lang") || "en");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("berdhill_lang", l);
  }, []);

  const t = useCallback((key, vars = {}) => {
    const fallback = DICT.en[key] ?? key;
    let str = (DICT[lang] && DICT[lang][key]) ?? fallback;
    for (const [k, v] of Object.entries(vars)) str = str.replace(`{${k}}`, v);
    return str;
  }, [lang]);

  // tr() — picks localized value from a content field that may be
  // (a) a string (legacy) or (b) { value, i18n: { al: "..." } } or
  // (c) a settings dict { value, i18n }
  const tr = useCallback((field, fallback = "") => {
    if (field == null) return fallback;
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      const i18n = field.i18n || field;
      if (i18n[lang] && typeof i18n[lang] === "string") return i18n[lang];
      if (i18n[lang] && typeof i18n[lang] === "object") return i18n[lang];
      return field.value ?? fallback;
    }
    return fallback;
  }, [lang]);

  // localized field from a room/policy doc: returns doc[field] OR doc.i18n[lang][field]
  const trField = useCallback((doc, field) => {
    if (!doc) return "";
    const i18n = doc.i18n || {};
    if (i18n[lang] && i18n[lang][field] != null) return i18n[lang][field];
    return doc[field] ?? "";
  }, [lang]);

  // localized settings value: settings is { key: { value, i18n: { lang: "..." } } }
  const trSetting = useCallback((settings, key) => {
    const item = settings?.[key];
    if (!item) return "";
    if (typeof item === "string") return item;
    const i18n = item.i18n || {};
    return i18n[lang] || item.value || "";
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, tr, trField, trSetting }), [lang, setLang, t, tr, trField, trSetting]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
